export type SeeristHttpMethod = 'GET' | 'POST';

export type SeeristEndpointKind =
  | 'search'
  | 'summarize'
  | 'askAnna'
  | 'byId'
  | 'custom'
  | 'customGet';

export type SeeristEndpoint = {
  id: string;
  label: string;
  method: SeeristHttpMethod;
  path: string;
  kind: SeeristEndpointKind;
  hint: string;
};

export type SeeristRequest = {
  endpointId: string;
  method: SeeristHttpMethod;
  path: string;
  query?: Record<string, string | number>;
  body?: Record<string, unknown>;
  advancedBody?: boolean;
};

export const SEERIST_ENDPOINTS: SeeristEndpoint[] = [
  {
    id: 'news-social-search',
    label: 'POST /content/news-social/search',
    method: 'POST',
    path: '/content/news-social/search',
    kind: 'search',
    hint: 'Open-source intelligence from news and social media.',
  },
  {
    id: 'news-social-by-id',
    label: 'GET /content/news-social/{id}',
    method: 'GET',
    path: '/content/news-social/{id}',
    kind: 'byId',
    hint: 'Retrieve a single news/social document by UUID.',
  },
  {
    id: 'news-social-summarize',
    label: 'POST /content/news-social/search/summarize',
    method: 'POST',
    path: '/content/news-social/search/summarize',
    kind: 'summarize',
    hint: 'AI summary (5w1h / bluf / sitrep). Needs at least 3 matches.',
  },
  {
    id: 'news-social-ask-anna',
    label: 'POST /content/news-social/search/ask-anna',
    method: 'POST',
    path: '/content/news-social/search/ask-anna',
    kind: 'askAnna',
    hint: 'Natural language question answered from matching documents.',
  },
  {
    id: 'news-search',
    label: 'POST /content/news/search',
    method: 'POST',
    path: '/content/news/search',
    kind: 'search',
    hint: 'News-only slice of the News & Social group.',
  },
  {
    id: 'social-search',
    label: 'POST /content/social/search',
    method: 'POST',
    path: '/content/social/search',
    kind: 'search',
    hint: 'Social-only slice of the News & Social group.',
  },
  {
    id: 'content-search',
    label: 'POST /content/search',
    method: 'POST',
    path: '/content/search',
    kind: 'search',
    hint: 'All content types in one index.',
  },
  {
    id: 'analysis-search',
    label: 'POST /content/analysis/search',
    method: 'POST',
    path: '/content/analysis/search',
    kind: 'search',
    hint: 'Analyst-authored research and intelligence reports.',
  },
  {
    id: 'discoverai-search',
    label: 'POST /content/discoverai/search',
    method: 'POST',
    path: '/content/discoverai/search',
    kind: 'search',
    hint: 'AI-generated news summaries and insights.',
  },
  {
    id: 'custom-post',
    label: 'POST — custom path',
    method: 'POST',
    path: '',
    kind: 'custom',
    hint: 'Any other documented POST endpoint under /content.',
  },
  {
    id: 'custom-get',
    label: 'GET — custom path',
    method: 'GET',
    path: '',
    kind: 'customGet',
    hint: 'Any other documented GET endpoint under /content.',
  },
];

export const QUERY_PARAMS_BY_KIND: Record<SeeristEndpointKind, string[]> = {
  search: ['page', 'perPage', 'sortBy', 'sortOrder', 'format', 'cursor'],
  summarize: ['summaryFormat', 'maxDocuments'],
  askAnna: [],
  byId: [],
  custom: ['page', 'perPage', 'sortBy', 'sortOrder', 'format', 'cursor'],
  customGet: [],
};

export const DATE_FIELDS = [
  'occurredAt',
  'publishedAt',
  'lastPublishedAt',
] as const;

export const RELATIVE_UNITS = ['hours', 'days', 'weeks', 'months'] as const;

export const LOCATION_KEYS = [
  'countries',
  'regions',
  'zones',
  'cities',
  'admin1',
  'counties',
  'waters',
] as const;

export const FACETS = [
  'riskTypes',
  'topics',
  'subtopics',
  'category',
  'subcategory',
  'severity',
  'severityScore',
  'countries',
  'regions',
  'zones',
  'cities',
  'admin1',
  'counties',
  'waters',
  'ports',
  'type',
  'language',
  'provider',
  'sources',
  'sourceHighRisk',
  'author',
  'tags',
  'assetTypes',
  'attackType',
  'locationPrecision',
  'entityPersons',
  'entityOrgs',
  'entityLocations',
  'perpetratorDetails',
];

export const DEFAULT_AGGREGATIONS = ['countries', 'riskTypes', 'topics'];

export const EXAMPLE_SEERIST_BODY: Record<string, unknown> = {
  publishedAt: { from: { value: -7, unit: 'days' } },
  aggregations: [...DEFAULT_AGGREGATIONS],
  facetMode: 'counts',
};

export const DEFAULT_SEERIST_REQUEST: SeeristRequest = {
  endpointId: 'news-social-search',
  method: 'POST',
  path: '/content/news-social/search',
  query: {
    page: 1,
    perPage: 20,
    sortBy: 'publishedAt',
    sortOrder: 'desc',
    format: 'list',
  },
  body: { ...EXAMPLE_SEERIST_BODY },
  advancedBody: false,
};

export function getSeeristEndpoint(endpointId: string): SeeristEndpoint {
  return (
    SEERIST_ENDPOINTS.find((e) => e.id === endpointId) || SEERIST_ENDPOINTS[0]
  );
}

export function seeristRequestLabel(request?: SeeristRequest | null): string {
  if (!request?.path && !request?.endpointId) return 'Seerist';
  const endpoint = getSeeristEndpoint(request.endpointId || '');
  const path = request.path || endpoint.path || endpoint.id;
  const short = path.replace(/^\/content\//, '').replace(/^\//, '');
  return `Seerist (${short || endpoint.id})`;
}
